import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Account({ me, reload }) {
  const { user } = useAuth();
  const [biz, setBiz] = useState({ name: "", phone: "", address: "", city: "", postcode: "", logo_url: "", accent_color: "" });
  const [acct, setAcct] = useState({ name: "", email: "", current_password: "", new_password: "" });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  useEffect(() => { if (me) setBiz({ name: me.name || "", phone: me.phone || "", address: me.address || "", city: me.city || "", postcode: me.postcode || "", logo_url: me.logo_url || "", accent_color: me.accent_color || "" }); }, [me]);
  useEffect(() => { if (user) setAcct((a) => ({ ...a, name: user.name || "", email: user.email || "" })); }, [user]);

  const saveBiz = async () => { try { await api.put("/business/profile", biz); toast.success("Business profile saved"); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/business/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBiz((b) => ({ ...b, logo_url: data.logo_url }));
      toast.success("Logo uploaded"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingLogo(false); }
  };
  const logoSrc = biz.logo_url ? (biz.logo_url.startsWith("http") ? biz.logo_url : fileUrl(biz.logo_url)) : null;
  const saveAcct = async () => {
    try { await api.put("/auth/profile", acct); toast.success("Account updated"); setAcct((a) => ({ ...a, current_password: "", new_password: "" })); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Settings</h1>
      <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading font-bold">Business profile</h2>
        <div className="space-y-1.5"><Label>Business name</Label><Input value={biz.name} onChange={(e) => setBiz((b) => ({ ...b, name: e.target.value }))} data-testid="biz-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Phone</Label><Input value={biz.phone} onChange={(e) => setBiz((b) => ({ ...b, phone: e.target.value }))} data-testid="biz-phone" /></div>
          <div className="space-y-1.5"><Label>City</Label><Input value={biz.city} onChange={(e) => setBiz((b) => ({ ...b, city: e.target.value }))} data-testid="biz-city" /></div>
        </div>
        <div className="space-y-1.5"><Label>Postcode</Label><Input value={biz.postcode} onChange={(e) => setBiz((b) => ({ ...b, postcode: e.target.value }))} data-testid="biz-postcode" /></div>
        <div className="space-y-1.5"><Label>Address</Label><Input value={biz.address} onChange={(e) => setBiz((b) => ({ ...b, address: e.target.value }))} data-testid="biz-address" /></div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-700">Client portal branding</p>
          <p className="mt-0.5 text-xs text-slate-500">Your logo and accent colour appear across your clients&rsquo; portal.</p>
          <div className="mt-3 flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
              {logoSrc ? <img src={logoSrc} alt="Logo" className="h-full w-full object-contain" data-testid="biz-logo-preview" /> : <span className="text-xs text-slate-300">No logo</span>}
            </div>
            <div>
              <input type="file" accept="image/*" className="hidden" id="biz-logo-input" data-testid="biz-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              <label htmlFor="biz-logo-input" className="inline-flex cursor-pointer items-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-brand hover:text-brand" data-testid="biz-logo-upload">{uploadingLogo ? "Uploading…" : "Upload logo"}</label>
              {biz.logo_url && <button onClick={() => setBiz((b) => ({ ...b, logo_url: "" }))} className="ml-2 text-xs text-slate-400 hover:text-red-500" data-testid="biz-logo-remove">Remove</button>}
              <p className="mt-1 text-xs text-slate-400">PNG or SVG, up to 4 MB.</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Label className="mb-0">Accent colour</Label>
            <input type="color" value={biz.accent_color || "#0EA5A4"} onChange={(e) => setBiz((b) => ({ ...b, accent_color: e.target.value }))} className="h-9 w-14 cursor-pointer rounded-md border border-slate-200 bg-white p-1" data-testid="biz-accent-color" />
            <Input value={biz.accent_color || ""} onChange={(e) => setBiz((b) => ({ ...b, accent_color: e.target.value }))} placeholder="#0EA5A4" className="w-32" data-testid="biz-accent-hex" />
            {biz.accent_color && <button onClick={() => setBiz((b) => ({ ...b, accent_color: "" }))} className="text-xs text-slate-400 hover:text-red-500" data-testid="biz-accent-reset">Reset</button>}
          </div>
        </div>
        <Button onClick={saveBiz} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="biz-save-btn">Save business</Button>
      </div>
      <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading font-bold">My account</h2>
        <div className="space-y-1.5"><Label>Name</Label><Input value={acct.name} onChange={(e) => setAcct((a) => ({ ...a, name: e.target.value }))} data-testid="acct-name" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input value={acct.email} onChange={(e) => setAcct((a) => ({ ...a, email: e.target.value }))} data-testid="acct-email" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={acct.current_password} onChange={(e) => setAcct((a) => ({ ...a, current_password: e.target.value }))} data-testid="acct-current-pw" /></div>
          <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={acct.new_password} onChange={(e) => setAcct((a) => ({ ...a, new_password: e.target.value }))} data-testid="acct-new-pw" /></div>
        </div>
        <Button onClick={saveAcct} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="acct-save-btn">Update account</Button>
      </div>
      <OnlinePayments reload={reload} />
      <SmsSettings reload={reload} />
      <NotificationSettings />
      <TeamSettings />
    </div>
  );
}

function TeamSettings() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");
  const load = useCallback(() => api.get("/business/staff").then((r) => setStaff(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    try {
      await api.post("/business/staff", { name: form.name, email: form.email, origin_url: window.location.origin });
      toast.success("Invite sent — they'll get a link to set their password");
      setForm({ name: "", email: "" }); setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? They'll lose access immediately.`)) return;
    try { await api.delete(`/business/staff/${s.id}`); toast.success("Team member removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setPassword = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.put(`/business/staff/${pwFor.id}/password`, { password: pw }); toast.success(`Password updated for ${pwFor.name}`); setPwFor(null); setPw(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="team-settings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Users className="h-5 w-5 text-brand" /><h2 className="font-heading font-bold">Team &amp; admins</h2></div>
        <Button size="sm" onClick={() => setAdding(true)} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="staff-add-btn"><Plus className="mr-1 h-4 w-4" /> Add member</Button>
      </div>
      <p className="text-sm text-slate-500">Everyone here has full access to this laundry's dashboard.</p>
      <div className="divide-y divide-slate-100">
        {staff.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-2.5" data-testid={`staff-row-${s.id}`}>
            <div>
              <p className="font-medium text-slate-800">{s.name} {s.is_self && <span className="text-xs text-slate-400">(you)</span>} {s.is_owner && <span className="ml-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-700">Owner</span>}</p>
              <p className="text-xs text-slate-500">{s.email}</p>
            </div>
            <div className="flex items-center gap-1.5">
              {!s.is_self && <button onClick={() => { setPwFor(s); setPw(""); }} aria-label={`Set password for ${s.name}`} title="Set password" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-brand" data-testid={`staff-pw-${s.id}`}><KeyRound className="h-4 w-4" /></button>}
              {!s.is_owner && !s.is_self && <button onClick={() => remove(s)} aria-label={`Remove ${s.name}`} title="Remove" className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-500" data-testid={`staff-remove-${s.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent data-testid="staff-add-dialog">
          <DialogHeader><DialogTitle>Add team member</DialogTitle><DialogDescription>They'll receive an email with a link to set their own password.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="staff-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="staff-email" /></div>
          </div>
          <DialogFooter><Button onClick={add} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="staff-add-save">Send invite</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent data-testid="staff-pw-dialog">
          <DialogHeader><DialogTitle>Set a new password</DialogTitle><DialogDescription>Set a new password for {pwFor?.name} ({pwFor?.email}).</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="staff-pw-input" /></div>
          <DialogFooter><Button onClick={setPassword} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="staff-pw-save">Update password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotificationSettings() {
  const [n, setN] = useState(null);
  useEffect(() => { api.get("/business/notification-settings").then((r) => setN(r.data)); }, []);
  if (!n) return null;
  const toggle = (chan, status) => setN((x) => {
    const list = x[chan].includes(status) ? x[chan].filter((s) => s !== status) : [...x[chan], status];
    return { ...x, [chan]: list };
  });
  const save = async () => { await api.put("/business/notification-settings", { email: n.email, sms: n.sms }); toast.success("Notification preferences saved"); };
  return (
    <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="notification-settings-card">
      <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-brand" /><h2 className="font-heading font-bold">Client notifications</h2></div>
      <p className="text-sm text-slate-500">Choose which order statuses trigger an email or SMS to the client.</p>
      <div className="overflow-hidden rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-center">Email</th><th className="px-3 py-2 text-center">SMS</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {n.statuses.map((s) => (
              <tr key={s}>
                <td className="px-3 py-2 capitalize">{s.replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-center"><input type="checkbox" checked={n.email.includes(s)} onChange={() => toggle("email", s)} data-testid={`notify-email-${s}`} /></td>
                <td className="px-3 py-2 text-center"><input type="checkbox" checked={n.sms.includes(s)} onChange={() => toggle("sms", s)} data-testid={`notify-sms-${s}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button onClick={save} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="notify-save">Save preferences</Button>
    </div>
  );
}

function SmsSettings({ reload }) {
  const [ss, setSs] = useState(null);
  const [keys, setKeys] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/business/sms-settings").then((r) => setSs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!keys.account_sid || !keys.auth_token || !keys.from_number) return toast.error("All Twilio fields are required");
    setBusy(true);
    try { await api.put("/business/sms-settings", keys); toast.success("SMS connected"); setKeys({ account_sid: "", auth_token: "", from_number: "" }); load(); reload && reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect SMS? Clients won't get text alerts.")) return;
    await api.delete("/business/sms-settings"); toast.success("Disconnected"); load(); reload && reload();
  };

  return (
    <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="sms-settings-card">
      <div className="flex items-center gap-2">
        <SmartphoneIcon />
        <h2 className="font-heading font-bold">SMS alerts</h2>
        {ss?.connected && <Badge className="border-0 bg-green-100 text-green-700">Connected</Badge>}
      </div>
      <p className="text-sm text-slate-500">Connect your own Twilio number to text clients automatically when an order is ready or out for delivery.</p>
      {ss === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : ss.connected ? (
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 text-sm">
          <p className="font-medium text-slate-800">Sending from {ss.from_number}</p>
          <p className="text-slate-500">Account SID: {ss.account_sid_masked}</p>
          <Button onClick={disconnect} variant="outline" className="mt-3 rounded-full border-red-200 bg-white text-red-600 hover:bg-red-50" data-testid="sms-disconnect-btn">Disconnect</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Twilio Account SID</Label><Input value={keys.account_sid} onChange={(e) => setKeys((k) => ({ ...k, account_sid: e.target.value }))} data-testid="sms-account-sid" placeholder="AC…" /></div>
          <div className="space-y-1.5"><Label>Auth Token</Label><Input type="password" value={keys.auth_token} onChange={(e) => setKeys((k) => ({ ...k, auth_token: e.target.value }))} data-testid="sms-auth-token" /></div>
          <div className="space-y-1.5"><Label>From number (E.164)</Label><Input value={keys.from_number} onChange={(e) => setKeys((k) => ({ ...k, from_number: e.target.value }))} data-testid="sms-from-number" placeholder="+44…" /></div>
          <Button onClick={connect} disabled={busy} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="sms-connect-btn">{busy ? "Verifying…" : "Connect SMS"}</Button>
        </div>
      )}
    </div>
  );
}

function SmartphoneIcon() {
  return <MessageSquare className="h-5 w-5 text-brand" />;
}

function OnlinePayments({ reload }) {
  const [ps, setPs] = useState(null);
  const [keys, setKeys] = useState({ secret_key: "", publishable_key: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/business/payment-settings").then((r) => setPs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!keys.secret_key.trim()) return toast.error("Paste your Stripe secret key");
    setBusy(true);
    try { const { data } = await api.put("/business/payment-settings", keys);
      toast.success(`Connected to ${data.account_name}`); setKeys({ secret_key: "", publishable_key: "" }); load(); reload && reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Stripe? Clients won't be able to pay invoices online.")) return;
    await api.delete("/business/payment-settings"); toast.success("Disconnected"); load(); reload && reload();
  };

  return (
    <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="online-payments-card">
      <div className="flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-brand" />
        <h2 className="font-heading font-bold">Online payments</h2>
        {ps?.connected && <Badge className="border-0 bg-green-100 text-green-700">Connected</Badge>}
      </div>
      <p className="text-sm text-slate-500">Connect your own Stripe account so your clients can pay invoices online. Payments go straight to your Stripe balance — LaundryDiary never touches the money.</p>
      {ps === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : ps.connected ? (
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 text-sm">
          <p className="font-medium text-slate-800">{ps.account_name}</p>
          <p className="text-slate-500">Secret key: {ps.secret_key_masked}</p>
          <Button onClick={disconnect} variant="outline" className="mt-3 rounded-full border-red-200 bg-white text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="stripe-disconnect-btn">Disconnect</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Stripe secret key</Label><Input type="password" placeholder="sk_live_… or sk_test_…" value={keys.secret_key} onChange={(e) => setKeys((k) => ({ ...k, secret_key: e.target.value }))} data-testid="stripe-secret-key" /></div>
          <div className="space-y-1.5"><Label>Publishable key (optional)</Label><Input placeholder="pk_…" value={keys.publishable_key} onChange={(e) => setKeys((k) => ({ ...k, publishable_key: e.target.value }))} data-testid="stripe-publishable-key" /></div>
          <p className="text-xs text-slate-400">Find these in your Stripe Dashboard → Developers → API keys.</p>
          <Button onClick={connect} disabled={busy} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="stripe-connect-btn">{busy ? "Verifying…" : "Connect Stripe"}</Button>
        </div>
      )}
    </div>
  );
}


export default Account;
